/**
 * `/o/:org/connect/authorize` — the page a person is sent to when a client asks
 * for authority. Until now the API could record a consent decision but nothing
 * could ask for one, so a client could never be authorized by a human.
 *
 * WHAT IS TRUSTED, AND FROM WHERE
 *   Every field in the query string is attacker-controlled: `client_id`,
 *   `redirect_uri`, `code_challenge`, `state` and every `capabilities` value.
 *   They are treated as strings this page DISPLAYS. The organization and the
 *   actor come from the verified session — `state.organization`, read from
 *   `/v1/organizations/current` under the bearer token — and never from the
 *   `:org` path segment, which is a slug in a URL anybody can edit. The URL is
 *   where the grant is recorded against; the session is who is granting it.
 *
 * WHY EVERY REQUESTED CAPABILITY IS RENDERED, ONE ROW EACH, VERBATIM
 *   A consent screen that renders a summary ("this client will be able to read
 *   skills") is a screen that can be wrong while looking right: a request for
 *   `skills:propose` answered with a paragraph about reading is a person
 *   approving something other than what was asked. So there is no summary here
 *   and no fixed scope string anywhere in this module. `test/consent.test.ts`
 *   asserts the `skills:propose` request renders `skills:propose` and not
 *   `skills:read`, which is what a hardcoded scope would fail.
 *
 * WHY AN UNRECOGNISED CAPABILITY BLOCKS APPROVAL INSTEAD OF BEING DROPPED
 *   Dropping it silently would grant a client strictly less than it asked for
 *   while showing the person the request they sent — a lie of omission. The
 *   server would refuse it anyway (`readCapabilities`, 422). So the value is
 *   shown as unrecognised and approval is withheld, which is the only outcome
 *   that is both true and reversible by the client.
 *
 * WHY NO APPROVE BUTTON IS RENDERED FOR A BAD PKCE REQUEST
 *   A disabled button with a tooltip is an affordance that lies on touch and
 *   in a screen reader: the control is there, it looks pressable, and pressing
 *   it does nothing. `consent.ts` requires S256 and refuses `plain`. This page
 *   refuses the request in the same terms and renders no control at all.
 *
 * WHY THE RETURN ADDRESS IS NEVER FOLLOWED
 *   This service mints no authorization code. `mcp/oauth/routes.ts` publishes
 *   discovery metadata that redirects to the provider, and no token endpoint,
 *   because Supabase Auth is the authorization server (V1 §7). Redirecting to
 *   `redirect_uri` with an invented `code` would be the single worst thing this
 *   page could do: the client would believe it holds a grant it does not have.
 *   So the decision is stated on the page and the client is told, plainly, that
 *   it must finish its own flow.
 *
 * WHY THE CONSENT CALL LIVES HERE
 *   It is the one write this page makes, and `api/queries.ts` has a single
 *   writer who is not in this file. The body is built here, next to the rules
 *   that decide what may be sent, so that the one place that can approve
 *   something is the same place that renders why.
 */

import type { AppShell, ScreenState } from '../app.js';
import type { ApiEnvelope, Result } from '../api/client.js';
import { fail, ok } from '../api/client.js';
import type { ClientGrantRow } from '../api/queries.js';
import { type RawHtml, html } from '../lib/html.js';
import { type PeopleDirectory, formatDateTime, personLabel } from '../lib/format.js';
import { button, docHead, emptyState, failurePanel, tag } from '../ui/components.js';

/**
 * The capability vocabulary, transcribed from the CHECK constraint
 * `client_grants_capabilities_known` (0001_schema.sql:580) exactly as
 * `apps/api/src/mcp/oauth/scopes.ts` transcribes it. It is used to decide what
 * is shown as unrecognised and is never rendered as a list: a page that prints
 * the vocabulary is a page that shows a person capabilities nobody asked for.
 */
const KNOWN_CAPABILITIES: readonly string[] = ['skills:read', 'skills:propose', 'skills:edit-draft'];

/** RFC 7636 S256: the base64url encoding of a SHA-256 digest, unpadded. */
const S256_CHALLENGE = /^[A-Za-z0-9\-_]{43}$/;

const LEDE =
  'Read what this client asked for before you answer. Nothing is granted until you press Approve, and the answer is recorded against your own identity.';

export type ConsentDecision = 'approve' | 'deny';

/** The authorization request as it arrived, all of it attacker-controlled. */
export interface AuthorizeRequest {
  readonly clientId: string;
  readonly redirectUri: string;
  readonly codeChallenge: string;
  readonly codeChallengeMethod: string;
  readonly state: string;
  /** Every `capabilities` value, verbatim and in the order it was sent. */
  readonly capabilities: readonly string[];
}

/** What `POST /mcp/oauth/consents` answers with, snake_case as the API sends it. */
export interface ConsentOutcome {
  readonly granted: boolean;
  readonly client_id: string;
  readonly org_id: string;
  readonly actor_id: string;
  readonly capabilities: readonly string[];
  readonly grant_id: string | null;
}

export interface SelectedOrganization {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
}

export interface AuthorizeData {
  readonly request: AuthorizeRequest;
  /** From the verified session. Null means nothing can be granted. */
  readonly organization: SelectedOrganization | null;
  /** The caller's live grant for this client, if one already exists. */
  readonly existingGrant: ClientGrantRow | null;
  /** Set once a decision has been recorded; null while the page is a question. */
  readonly outcome: ConsentOutcome | null;
}

/**
 * Read the request out of the query string.
 *
 * THE PARAMETER IS `capability`, SINGULAR, because that is what an
 * authorization request carries. The API's own reader
 * (`apps/api/src/mcp/oauth/authorization.ts`, `readCapabilityList`) accepts
 * `capability` repeated and comma-joined, and this page used to read
 * `capabilities` instead — a different parameter, so a request that named a
 * capability rendered here as "this request named no capability", which is a
 * consent screen misreporting the very request it is asking about.
 *
 * Both spellings of the VALUE are accepted for the same reason the API accepts
 * both: a query string built by hand repeats the parameter and one built by a
 * library joins it. Reading only one of them would mean a client that asked
 * for two capabilities by either spelling got one.
 */
export function parseAuthorizeRequest(search: string): AuthorizeRequest {
  const params = new URLSearchParams(search);
  return {
    clientId: params.get('client_id') ?? '',
    redirectUri: params.get('redirect_uri') ?? '',
    codeChallenge: params.get('code_challenge') ?? '',
    codeChallengeMethod: params.get('code_challenge_method') ?? '',
    state: params.get('state') ?? '',
    capabilities: params
      .getAll('capability')
      .flatMap((entry) => entry.split(','))
      .map((entry) => entry.trim())
      .filter((entry) => entry !== ''),
  };
}

/** The capabilities in this request that the vocabulary does not contain. */
export function unrecognisedCapabilities(request: AuthorizeRequest): readonly string[] {
  return request.capabilities.filter(
    (capability) => !KNOWN_CAPABILITIES.includes(capability),
  );
}

export type PkceVerdict =
  | { readonly ok: true }
  | { readonly ok: false; readonly reason: string };

/**
 * PKCE, checked the way the API checks it.
 *
 * `plain` is refused, not downgraded: it protects nothing against a code
 * interception, which is the only attack the parameter exists for.
 */
export function checkPkce(request: AuthorizeRequest): PkceVerdict {
  if (request.codeChallengeMethod === '') {
    return {
      ok: false,
      reason:
        'The request sent no code_challenge_method. This service accepts S256 only, so there is no ' +
        'safe way to read this request.',
    };
  }
  if (request.codeChallengeMethod !== 'S256') {
    return {
      ok: false,
      reason: `The request asked for code_challenge_method "${request.codeChallengeMethod}". Only S256 is accepted, so this request cannot be approved.`,
    };
  }
  if (!S256_CHALLENGE.test(request.codeChallenge)) {
    return {
      ok: false,
      reason:
        'The code_challenge is not a 43 character base64url SHA-256 challenge, so it cannot be the ' +
        'digest this method requires.',
    };
  }
  return { ok: true };
}

/** Everything that stands between this request and an Approve button. */
export function approvalBlockers(data: AuthorizeData): readonly string[] {
  const blockers: string[] = [];
  if (data.organization === null) {
    blockers.push(
      'No organization is selected for this session, so there is nothing to grant against. Sign in ' +
        'to the organization the client named.',
    );
  }
  if (data.request.clientId === '') {
    blockers.push('The request names no client id.');
  }
  const pkce = checkPkce(data.request);
  if (!pkce.ok) blockers.push(pkce.reason);
  for (const capability of unrecognisedCapabilities(data.request)) {
    blockers.push(
      `"${capability}" is not a capability this service knows, so it cannot be granted. The client ` +
        'must ask again with a capability from the vocabulary its registration published.',
    );
  }
  return blockers;
}

/**
 * Assemble the page's data.
 *
 * No request is made. Everything this screen shows is either the request (from
 * the URL) or a fact about the verified session (`state.organization`) or a row
 * the shell already loaded (`state.grants`). `app.ts` requires a `Result` from
 * every loader, so one is returned; the refusals on this screen are refusals to
 * APPROVE, not failures to load, and they live in the data where the renderer
 * and the tests can both see them.
 */
export async function loadAuthorize(app: AppShell, orgId: string): Promise<Result<AuthorizeData>> {
  const request = parseAuthorizeRequest(app.state.search);
  const organization = app.state.organization;
  // The newest row for this client in THIS organization, so the revocation
  // control is offered for the grant a client would actually be checked
  // against, and `orgId` is never taken from the URL.
  const existingGrant =
    app.state.grants
      .filter((grant) => grant.org_id === orgId && grant.client_id === request.clientId)
      .sort((left, right) => right.created_at.localeCompare(left.created_at))[0] ?? null;

  return ok({
    request,
    organization:
      organization === null
        ? null
        : { id: organization.id, name: organization.name, slug: organization.slug },
    existingGrant,
    outcome: null,
  });
}

export function renderAuthorize(app: AppShell, screen: ScreenState): RawHtml {
  if (screen.kind === 'loading' || screen.kind === 'idle') {
    return html`
      ${docHead([{ label: 'Section', value: 'Authorize a client' }], 'Authorize a client')}
      <div class="panel"><p class="cell-note">Loading the authorization request.</p></div>
    `;
  }
  if (screen.kind === 'failed') {
    return html`
      ${docHead([{ label: 'Section', value: 'Authorize a client' }], 'Authorize a client')}
      ${failurePanel(screen.failure, { retry: 'authorize:reload' })}
    `;
  }

  const data = screen.data as AuthorizeData;
  const entries = [
    { label: 'Section', value: 'Authorize a client' },
    { label: 'Organization', value: data.organization?.slug ?? 'not selected' },
    { label: 'Client', value: data.request.clientId === '' ? 'not named' : data.request.clientId },
  ];

  // Once a decision is recorded this page stops asking. The request is not
  // re-rendered beside the outcome because the outcome's own fields are not
  // safe to read next to it: on a decline the API still returns the requested
  // capabilities, the organization and the actor, and none of those describe a
  // grant that exists.
  if (data.outcome !== null) {
    return html`
      ${docHead(entries, 'Authorization decided', LEDE)}
      ${outcomePanel(app.state.directory, data)}
    `;
  }

  const blockers = approvalBlockers(data);
  return html`
    ${docHead(entries, 'Authorize a client', LEDE)}

    <div class="split">
      <div>
        ${organizationPanel(data)}
        ${requestPanel(data.request)}
        ${capabilityPanel(data.request)}
        ${existingGrantPanel(app.state.directory, data.existingGrant)}
        ${decisionPanel(data, blockers)}
      </div>
      <div>
        ${afterPanel()}
      </div>
    </div>
  `;
}

function organizationPanel(data: AuthorizeData): RawHtml {
  const organization = data.organization;
  if (organization === null) {
    return html`
      <div class="panel">
        ${emptyState({
          title: 'No organization is selected',
          body: 'The grant would be recorded against the organization your session is verified for, and this session has none. Nothing can be approved from here.',
          primary: null,
          secondary: [],
        })}
      </div>
    `;
  }
  return html`
    <div class="panel">
      <div class="panel-head">
        <h2>Organization</h2>
        ${tag('From your session', 'S', 'accent')}
      </div>
      <div class="panel-body">
        <dl class="dl">
          <dt>Name</dt>
          <dd>${organization.name}</dd>
          <dt>Slug</dt>
          <dd><code>${organization.slug}</code></dd>
        </dl>
        <p class="cell-note">
          Read from the organization your sign-in is bound to, not from the address bar. A link with
          any organization in it still grants against this one.
        </p>
      </div>
    </div>
  `;
}

function requestPanel(request: AuthorizeRequest): RawHtml {
  return html`
    <div class="panel">
      <div class="panel-head"><h2>What this client asked for</h2></div>
      <div class="panel-body">
        <dl class="dl">
          <dt>Client id</dt>
          <dd>${request.clientId === '' ? 'not sent' : html`<code>${request.clientId}</code>`}</dd>
          <dt>Return address</dt>
          <dd>${request.redirectUri === '' ? 'not sent' : html`<code>${request.redirectUri}</code>`}</dd>
          <dt>State</dt>
          <dd>${request.state === '' ? 'not sent' : html`<code>${request.state}</code>`}</dd>
          <dt>Code challenge method</dt>
          <dd>${request.codeChallengeMethod === '' ? 'not sent' : html`<code>${request.codeChallengeMethod}</code>`}</dd>
          <dt>Code challenge</dt>
          <dd>${request.codeChallenge === '' ? 'not sent' : html`<code>${request.codeChallenge}</code>`}</dd>
        </dl>
        <p class="cell-note">
          The return address is shown as text and nothing on this page follows it. See the panel on
          the right for why.
        </p>
      </div>
    </div>
  `;
}

/**
 * One row per requested capability, verbatim.
 *
 * The read-only sentence below is only rendered when the request named nothing
 * at all, because "no capability named" is the one case where the server's own
 * rule — not this page's — decides what the client ends up holding.
 */
function capabilityPanel(request: AuthorizeRequest): RawHtml {
  const unknown = unrecognisedCapabilities(request);
  if (request.capabilities.length === 0) {
    return html`
      <div class="panel">
        <div class="panel-head">
          <h2>Capabilities</h2>
          ${tag('None requested', 'C', 'plain')}
        </div>
        <div class="panel-body">
          <p>
            This request named no capability. A client that asks for nothing starts read-only: it
            will hold <code>skills:read</code> and nothing else. That is the server's rule (V1
            §7:255), applied when the field is absent, not a choice made on this page — this page
            cannot add a capability the request did not name.
          </p>
        </div>
      </div>
    `;
  }
  return html`
    <div class="panel">
      <div class="panel-head">
        <h2>Capabilities</h2>
        ${unknown.length === 0
          ? tag(`${request.capabilities.length} requested`, 'C', 'accent')
          : tag('Contains unrecognised', 'X', 'danger')}
      </div>
      <div class="panel-body">
        <ul class="stack-sm">
          ${request.capabilities.map(
            (capability) => html`
              <li>
                <code>${capability}</code>
                ${KNOWN_CAPABILITIES.includes(capability)
                  ? tag('Recognised', 'C', 'accent')
                  : tag('Unrecognised', 'X', 'danger')}
              </li>
            `,
          )}
        </ul>
        <p class="cell-note">
          Exactly these names are sent if you approve, unchanged. Nothing is added and nothing is
          dropped.
        </p>
      </div>
    </div>
  `;
}

/**
 * Why there is no Approve button, and the Deny that is always there.
 *
 * Deny stays available in every blocked state, including a malformed PKCE
 * request. `readConsentRequest` checks PKCE only for `approve` for the same
 * reason: a screen that made saying no hard would be worse than one that makes
 * saying yes slightly awkward.
 */
function decisionPanel(data: AuthorizeData, blockers: readonly string[]): RawHtml {
  const canAct = data.organization !== null;
  return html`
    <div class="panel">
      <div class="panel-head">
        <h2>Your decision</h2>
        ${blockers.length === 0 ? tag('Can be approved', 'C', 'accent') : tag('Cannot be approved', 'X', 'danger')}
      </div>
      <div class="panel-body stack-sm">
        ${blockers.length === 0
          ? html`<p>Approving records a grant against your identity and your organization.</p>`
          : html`
              <ul class="stack-sm">
                ${blockers.map((blocker) => html`<li>${blocker}</li>`)}
              </ul>
            `}
        <div class="btn-row">
          ${blockers.length === 0 ? button('Approve this client', 'authorize:approve', 'primary') : ''}
          ${canAct ? button('Decline this request', 'authorize:deny', 'secondary') : ''}
        </div>
      </div>
    </div>
  `;
}

/** The caller's existing grant for this client, with the control to end it. */
function existingGrantPanel(directory: PeopleDirectory, grant: ClientGrantRow | null): RawHtml {
  if (grant === null) return html``;
  const revoked = grant.revoked_at !== null;
  return html`
    <div class="panel">
      <div class="panel-head">
        <h2>A grant for this client already exists</h2>
        ${revoked ? tag('Revoked', 'X', 'danger') : tag('Live', 'C', 'accent')}
      </div>
      <div class="panel-body stack-sm">
        <dl class="dl">
          <dt>Client id</dt>
          <dd><code>${grant.client_id}</code></dd>
          <dt>Capabilities</dt>
          <dd>${grant.capabilities.join(', ')}</dd>
          <dt>Granted to</dt>
          <dd>${personLabel(grant.actor_id, directory)}</dd>
          <dt>Registered</dt>
          <dd>${formatDateTime(grant.created_at)}</dd>
        </dl>
        <div class="btn-row">
          ${revoked
            ? ''
            : button('Revoke this grant', `authorize:revoke:${grant.client_id}`, 'danger')}
        </div>
        <p class="cell-note">
          These are the capabilities this client holds today, which is not necessarily what it just
          asked for. Approving replaces them.
        </p>
      </div>
    </div>
  `;
}

function outcomePanel(directory: PeopleDirectory, data: AuthorizeData): RawHtml {
  const outcome = data.outcome;
  const slug = data.organization?.slug;
  const back =
    slug === undefined
      ? ''
      : html`<a class="btn btn-secondary" href="/o/${slug}/connect">Back to connected clients</a>`;
  if (outcome !== null && !outcome.granted) {
    // `granted: false` means the person pressed Deny. It is a completed
    // decision, not a failure, and the fields the API returns alongside it
    // (`capabilities`, `org_id`, `actor_id`) describe what was asked for — not
    // a grant. Nothing here renders them.
    return html`
      <div class="panel">
        <div class="panel-head">
          <h2>You declined this request</h2>
          ${tag('Declined', 'X', 'plain')}
        </div>
        <div class="panel-body stack-sm">
          <p>
            No grant was recorded for <code>${outcome.client_id}</code>. The request is answered and
            the client holds nothing from you.
          </p>
          <p>
            Nothing was sent to the client. This service mints no authorization code, so there is no
            result to return to its return address; the client completes its own authorization flow,
            and it will not receive one from here.
          </p>
          <div class="btn-row">${back}</div>
        </div>
      </div>
    `;
  }
  if (outcome === null) return html``;
  return html`
    <div class="panel">
      <div class="panel-head">
        <h2>Approved</h2>
        ${tag('Grant recorded', 'C', 'accent')}
      </div>
      <div class="panel-body stack-sm">
        <p>
          <code>${outcome.client_id}</code> may now act for ${personLabel(outcome.actor_id, directory)} in this
          organization with these capabilities and no others:
        </p>
        <ul class="stack-sm">
          ${outcome.capabilities.map((capability) => html`<li><code>${capability}</code></li>`)}
        </ul>
        <p class="cell-note">
          The grant is row ${outcome.grant_id === null ? 'unidentified' : outcome.grant_id} and it
          is in force on the client's next request.
        </p>
        <p>
          Nothing was sent to the client. This service mints no authorization code, so this page
          does not follow the return address: the client must complete its own authorization flow
          through the organization identity provider.
        </p>
        <div class="btn-row">${back}</div>
      </div>
    </div>
  `;
}

function afterPanel(): RawHtml {
  return html`
    <div class="panel">
      <div class="panel-head"><h2>What happens next</h2></div>
      <div class="panel-body stack-sm">
        <p>
          Approving records a grant, and this service mints no authorization code: Supabase Auth is
          the authorization server, and the discovery document at
          <code>/.well-known/oauth-authorization-server</code> points a client at it.
        </p>
        <p>
          So this page never navigates to the return address. A redirect carrying a code this
          service did not mint would hand the client something it cannot use, and report an
          authorization that did not happen.
        </p>
      </div>
    </div>
  `;
}

/**
 * Record the decision.
 *
 * The body is built here rather than in `api/queries.ts` so that the rules
 * about what may be sent are next to the rules about what may be shown.
 *
 * `capabilities` is omitted ENTIRELY when the request named none. Sending
 * `[]` would be refused (422: a grant cannot have zero capabilities) and
 * sending `['skills:read']` would be this page choosing the client's read-only
 * grant instead of the server applying its own default.
 */
export async function submitConsent(
  app: AppShell,
  data: AuthorizeData,
  decision: ConsentDecision,
): Promise<Result<ConsentOutcome>> {
  if (decision === 'approve' && approvalBlockers(data).length > 0) {
    // The control is not rendered in this state. The check is here so that a
    // handler reached any other way still cannot send an approval this page
    // has already told the person is unsafe.
    return fail({
      kind: 'invalid',
      reason: 'not_approvable',
      detail: 'This authorization request cannot be approved.',
    });
  }

  const body: Record<string, unknown> = {
    client_id: data.request.clientId,
    decision,
  };
  // An absent `state` is omitted rather than sent empty: the API reads an empty
  // string as no state, and sending it would claim the client sent one.
  if (data.request.state !== '') body.state = data.request.state;
  if (decision === 'approve') {
    if (data.request.capabilities.length > 0) {
      body.capabilities = [...data.request.capabilities];
    }
    body.code_challenge = data.request.codeChallenge;
    body.code_challenge_method = data.request.codeChallengeMethod;
  }

  const response = await app.client.api<ApiEnvelope<ConsentOutcome>>('/mcp/oauth/consents', {
    method: 'POST',
    body,
  });
  if (!response.ok) return response;
  return ok(response.value.data);
}
